import React, { useId } from 'react';
import { Textarea } from '../ui/Field.js';
import { TAB_EDIT_COPY, EDIT_NOTE_MAX_LENGTH } from '../../utils/tabEdits.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

export interface EditNoteFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  'data-testid'?: string;
  textareaTestId?: string;
  counterTestId?: string;
}

export const EditNoteField: React.FC<EditNoteFieldProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
  id: customId,
  'data-testid': dataTestId,
  textareaTestId,
  counterTestId
}) => {
  const generatedId = useId();
  const id = customId ?? `edit-note-field-${generatedId}`;
  const counterId = `${id}-counter`;
  const errorId = `${id}-error`;

  const isOverLimit = value.length > EDIT_NOTE_MAX_LENGTH;
  const atLimit = value.length >= EDIT_NOTE_MAX_LENGTH;

  const isDetailsNote = dataTestId === 'details-note';
  const containerTestId = isDetailsNote ? undefined : dataTestId;
  const resolvedTextareaTestId = textareaTestId ?? (isDetailsNote ? 'details-note' : 'edit-note');
  const resolvedCounterTestId = counterTestId ?? (isDetailsNote ? 'details-note-counter' : 'edit-note-counter');

  return (
    <div className={`flex flex-col gap-1.5 ${className}`} data-testid={containerTestId}>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-[13px] font-medium text-base-content/80">
          {TAB_EDIT_COPY.c11Label}
        </label>
        <span
          id={counterId}
          data-testid={resolvedCounterTestId}
          className={`text-xs font-mono tabular-nums ${
            atLimit ? 'text-error-soft font-semibold' : 'text-base-content/60'
          }`}
        >
          {toPersianDigits(value.length)}/{toPersianDigits(EDIT_NOTE_MAX_LENGTH)}
        </span>
      </div>

      <Textarea
        id={id}
        rows={2}
        dir="auto"
        value={value}
        disabled={disabled}
        placeholder={TAB_EDIT_COPY.c11Placeholder}
        error={isOverLimit}
        aria-invalid={isOverLimit}
        aria-describedby={isOverLimit ? `${errorId} ${counterId}` : counterId}
        onChange={(e) => onChange(e.target.value)}
        data-testid={resolvedTextareaTestId}
      />

      {isOverLimit && (
        <span
          id={errorId}
          role="alert"
          data-testid="edit-note-error"
          className="text-xs text-error-soft"
        >
          {TAB_EDIT_COPY.limitNoteMax}
        </span>
      )}
    </div>
  );
};

EditNoteField.displayName = 'EditNoteField';
